import React from 'react';
import { Link } from 'react-router-dom';
import LegalPolicyLayout from '@/components/legal/LegalPolicyLayout';
import { LEGAL_ENTITY, LEGAL_ROUTES } from '@/constants/legalEntity';

export const PrivacyPolicy: React.FC = () => {
  return (
    <LegalPolicyLayout title="Privacy Policy">
      <section className="mb-8">
        <h2>1. Introduction</h2>
        <p>
          {LEGAL_ENTITY.legalNameEn} (“we”, “us”) operates {LEGAL_ENTITY.productName}. This Privacy Policy
          explains how we collect, use, and protect personal data when you use our website and application.
        </p>
      </section>

      <section className="mb-8">
        <h2>2. Data we collect</h2>
        <ul>
          <li>
            <strong>Account data</strong> — name, email, role (teacher/student), studio/tenant association,
            profile photo where provided.
          </li>
          <li>
            <strong>Usage data</strong> — pages viewed, features used, lesson bookings, subscription status.
          </li>
          <li>
            <strong>Payment-related data</strong> — we receive transaction IDs, subscription status, and
            amounts from Lemon Squeezy; we do not store full card numbers on our servers.
          </li>
          <li>
            <strong>Communications</strong> — support messages and contact form submissions.
          </li>
          <li>
            <strong>AI practice</strong> — audio or files you upload for AI feedback, processed per feature
            settings.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2>3. How we use data</h2>
        <ul>
          <li>Provide and improve the Service</li>
          <li>Process payments and subscriptions via Lemon Squeezy</li>
          <li>Send service emails (invites, booking confirmations, billing notices)</li>
          <li>Support, security, and fraud prevention</li>
          <li>Analytics to understand product usage (see Cookie Policy)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2>4. Legal bases (EEA/UK users)</h2>
        <p>
          Where GDPR applies, we rely on contract performance, legitimate interests (security, product
          improvement), and consent where required (e.g. non-essential cookies).
        </p>
      </section>

      <section className="mb-8">
        <h2>5. Sharing with processors</h2>
        <ul>
          <li>
            <strong>Supabase</strong> — database and authentication
          </li>
          <li>
            <strong>Lemon Squeezy</strong> — payments, subscriptions, merchant of record
          </li>
          <li>
            <strong>Hosting / email</strong> — infrastructure and transactional email as configured for your
            deployment
          </li>
          <li>
            <strong>AI providers</strong> — when you use AI practice features (content processed to deliver
            feedback)
          </li>
        </ul>
        <p>We do not sell personal data.</p>
      </section>

      <section className="mb-8">
        <h2>6. Retention</h2>
        <p>
          We retain data while your account is active and as needed for legal, tax, and dispute resolution.
          You may request deletion subject to lawful retention requirements.
        </p>
      </section>

      <section className="mb-8">
        <h2>7. Your rights</h2>
        <p>
          Depending on your location, you may have rights to access, correct, delete, restrict, or port your
          data, and to object to certain processing. Contact {LEGAL_ENTITY.contactEmail}. You may also lodge a
          complaint with your local supervisory authority.
        </p>
      </section>

      <section className="mb-8">
        <h2>8. Cookies</h2>
        <p>
          See our <Link to={LEGAL_ROUTES.cookies}>Cookie Policy</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2>9. Children</h2>
        <p>
          Student accounts for minors may be created through a teacher’s studio. Teachers are responsible for
          obtaining appropriate parental consent where required by law.
        </p>
      </section>

      <section className="mb-8">
        <h2>10. Changes</h2>
        <p>We may update this policy; the “Last updated” date at the top will change.</p>
      </section>

      <section className="mb-8">
        <h2>11. Contact</h2>
        <p>
          {LEGAL_ENTITY.legalNameEn} · {LEGAL_ENTITY.contactEmail}
        </p>
      </section>
    </LegalPolicyLayout>
  );
};
